import { Box, IconButton, Typography, useTheme } from '@mui/material';
import { BiChevronLeft, BiChevronRight, BiCalendar, BiInfoCircle } from 'react-icons/bi';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import { Locations } from '@gql/base-types';
import { MovePlannerLocationSelect } from '@hopdrive/feature-move-planner';
import { ISchedulingAlgorithm } from '@hopdrive/sdk-appointments';

interface ScheduleHeaderProps {
  selectedDate: string; // YYYY-MM-DD format
  onDateChange: (date: string) => void;
  onAddressSelect?: (location: Locations | null, isNewLocation?: boolean) => void;
  locations?: Locations[];
  rooftopId?: number;
  selectedLocation?: Locations | null;
  isLocationClosed?: boolean;
  algorithm?: ISchedulingAlgorithm;
  isPlanning?: boolean;
  fullPlanningEnabled?: boolean;
  isLoadingOverlays?: boolean;
  noSlotsAvailable?: boolean;
  driverCount?: number;
}

const ScheduleHeader = ({
  selectedDate,
  onDateChange,
  onAddressSelect,
  locations = [],
  rooftopId,
  selectedLocation = null,
  isLocationClosed = false,
  algorithm,
  isPlanning = false,
  fullPlanningEnabled = false,
  isLoadingOverlays = false,
  noSlotsAvailable = false,
  driverCount = 1,
}: ScheduleHeaderProps) => {
  const theme = useTheme();

  const handlePreviousDay = () => {
    const prevDay = dayjs(selectedDate).subtract(1, 'day').format('YYYY-MM-DD');
    onDateChange(prevDay);
  };

  const handleNextDay = () => {
    const nextDay = dayjs(selectedDate).add(1, 'day').format('YYYY-MM-DD');
    onDateChange(nextDay);
  };

  const handleToday = () => {
    onDateChange(dayjs().format('YYYY-MM-DD'));
  };

  const handleDatePickerChange = (newValue: Dayjs | null) => {
    if (newValue) {
      onDateChange(newValue.format('YYYY-MM-DD'));
    }
  };

  const isToday = dayjs(selectedDate).isSame(dayjs(), 'day');
  const isTodayOrFuture = dayjs(selectedDate).isAfter(dayjs(), 'day') || isToday;

  return (
    <Box
      sx={{
        p: 2,
        bgcolor: theme.palette.background.paper,
        borderBottom: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 2,
      }}
    >
      {/* Left side - Date controls */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {/* Previous day button */}
        <IconButton onClick={handlePreviousDay} size="small">
          <BiChevronLeft size={24} />
        </IconButton>

        {/* Date picker */}
        <DatePicker
          value={dayjs(selectedDate)}
          onChange={handleDatePickerChange}
          format="ddd, MMM D, YYYY"
          slotProps={{
            textField: {
              size: 'small',
              sx: { width: 200 },
            },
          }}
        />

        {/* Next day button */}
        <IconButton onClick={handleNextDay} size="small">
          <BiChevronRight size={24} />
        </IconButton>

        {/* Today button */}
        {!isToday && (
          <IconButton
            onClick={handleToday}
            size="small"
            sx={{

              color: theme.palette.primary.main,
            }}
          >
            <BiCalendar size={24} />
          </IconButton>
        )}
      </Box>

      {/* Center - Algorithm description (shown when planning) */}
      {isPlanning && algorithm && !noSlotsAvailable && !isLocationClosed && driverCount > 1 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1,
            py: 1,
            bgcolor: theme.palette.info.main + '15',
            borderRadius: 1,
            border: `1px solid ${theme.palette.info.main}40`,
          }}
        >
          <BiInfoCircle size={16} color={theme.palette.info.main} />
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            <strong>Assignment Strategy:</strong> {algorithm.description}
          </Typography>
        </Box>
      )}

      {/* Right side - Address search (only for today and future dates, when not closed, and when full planning is enabled) */}
      {onAddressSelect && rooftopId && isTodayOrFuture && !isLocationClosed && fullPlanningEnabled && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Address Search */}
          <Box sx={{ width: 400 }}>
            <MovePlannerLocationSelect
              locations={locations}
              location={selectedLocation}
              rooftopId={rooftopId}
              onLocationChange={onAddressSelect}
              placeholder="Search customer address..."
              loading={isLoadingOverlays}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ScheduleHeader;
